import { useEffect, useState } from "react"
import { DynamicFormTemplate } from "../../utilities"
import { Form, Constant, Materials } from "../../constants";
import { useTranslation } from 'react-i18next';

const MaterialForm = (props) => {
    const { t: translate } = useTranslation();
    const { isHidden, formData, error } = props;

    const autoCompleteField = {
        "materiel-immat": true,
        "materiel-numserie": true,
        "materiel-numchassis": true,
        "materiel-numparc": true,
        "materiel-marque": true,
    };

    const notAutoCompleteOtherField = {
        "materiel-marque": true,
        "materiel-categorie": true,
    };

    const search = {
        "materiel-id": true,
        "materiel-immat": true,
        "materiel-numserie": true,
        "materiel-numchassis": true,
        "materiel-numparc": true
    };

    const characteristic = {
        "materiel-marque": true,
        "materiel-type": true,
        "materiel-categorie": true,
        "materiel-capacite": true,
        "materiel-centre_gravite": true,
        "materiel-mise_en_circulation": true,
        "materiel-annee_fabrication": true
    };

    const [boutonMateriel, setBoutonMateriel] = useState("");
    const [isCreateNew, setIsCreateNew] = useState(false);
    const [isPicked, setIsPicked] = useState(false);
    const [searchForm, setSearchForm] = useState([]);
    const [characteristicForm, setCharacteristicForm] = useState([]);
    const [material, setMaterial] = useState({});
    const [errorInput, setErrorInput] = useState({});

    const seperateForm = () => {
        const formConstant = Form.getForm();
        const temp = formConstant[localStorage.getItem(Constant.localStorageKey.formId)];
        const materialTitle = temp["_parametres_"].bouton_materiel.split(" ");
        if (materialTitle.length > 0) {
            setBoutonMateriel(`(${materialTitle[0]})`);
        }

        const searchTemp = [];
        const characteristicTemp = [];

        formData.forEach(element => {
            if (autoCompleteField[element.name]) {
                updateAutoCompleteSchema(element);
            }

            if (search[element.name]) {
                searchTemp.push(element);
            } else if (characteristic[element.name]) {
                characteristicTemp.push(element);
            }
        });

        setSearchForm(searchTemp);
        setCharacteristicForm(characteristicTemp);
    };

    const updateAutoCompleteSchema = (element) => {
        const options = [];
        element.typehtml = "autocomplete";
        element.allowFreeText = true;
        element.displayAllOption = false;

        if (notAutoCompleteOtherField[element.name]) {
            element.key = "";
            const uniqueItems = new Set(Materials.getMaterials().map(m => m[element.name.split("-").join("_")]));
            uniqueItems.forEach((material) => material ? options.push({ label: material, id: material }) : null);
        } else {
            element.key = "material";
            const materialName = Materials.getMaterials().map(m => ({
                label: m[element.name.split("-").join("_")],
                id: m["materiel_id"]
            }));
            materialName.forEach((material) => material.label ? options.push(material) : null);
        }

        element.options = options;
    };

    const renderInfo = (form) => {
        const rows = [];
        form.forEach(c => {
            if (c.typehtml === 'hidden') return;
            const propertyName = c.name.split("-").join("_");
            const hasError = errorInput[c.name]; 
            rows.push(
                <div key={c.name} className={`form-group form-element ${hasError ? "error" : ""}`}>
                    <p htmlFor={c.name}>
                        {c.label} {c.requis ? <span className='text-danger'>*</span> : null}
                    </p>
                    <p className="bold">{material[propertyName]}</p>
                </div>
            );
        });
        return rows;
    };

    const resetMaterial = () => {
        const materialTemp = { ...material };
        for (let key of Object.keys(materialTemp)) {
            materialTemp[key] = "";
        }
        setMaterial(materialTemp);
    };

    const setCreateNewMaterial = () => {
        setIsCreateNew(true);
        setIsPicked(false);
        resetMaterial();
        window.dispatchEvent(new Event(Constant.event.resetOperation));
    };

    const unSetCreateNewMaterial = () => {
        setIsCreateNew(false);
        setIsPicked(false);
        resetMaterial();
        window.dispatchEvent(new Event(Constant.event.resetOperation));
    };

    const modifiesMaterial = () => {
        setIsCreateNew(true);
        setIsPicked(false);
    };

    const setMaterials = (e) => {
        const { key, data } = e.detail;
        if (key === "material") {
            setIsPicked(true);
            setIsCreateNew(false);
            const materials = Materials.getMaterials();
            const filteredMaterials = materials.filter(m => m["materiel_id"] === data.id);
            setMaterial({ ...material, ...filteredMaterials[0] });
        }
    };

    // Gestion de la catégorie invalide ou vide
    useEffect(() => {
        const currentCategory = material?.materiel_categorie?.trim();
        const categoryField = formData.find(f => f.name === "materiel-categorie");
        const availableCategories = categoryField?.liste_valeurs
            ?.split(";")
            ?.map(v => v.trim())
            ?.filter(v => v.length > 0) || [];

        const isCategoryValid = currentCategory && availableCategories.includes(currentCategory);

        // ⚠️ N’ouvre le formulaire que si un matériel a été sélectionné
        if (isPicked && !isCategoryValid) {
            // Ajoute l'erreur pour le rapport
            setErrorInput(prev => ({ ...prev, "materiel-categorie": true }));

            // Vide le champ pour que le résumé considère la catégorie comme "vide" (rouge)
            if (currentCategory) {
                setMaterial(prev => ({
                    ...prev,
                    materiel_categorie: ""
                }));
            }

            // Force le passage en template de modification
            setIsCreateNew(true);
            setIsPicked(false);
        } else if (isCategoryValid) {
            setErrorInput(prev => {
                const newErrors = { ...prev };
                delete newErrors["materiel-categorie"];
                return newErrors;
            });
        }
    }, [material?.materiel_categorie, formData, isPicked]);

    useEffect(() => {
        if (error.length > 0) {
            let errorInputTemp = {};
            error.forEach(e => {
                errorInputTemp = { ...errorInputTemp, [e]: true };
            });
            setErrorInput(errorInputTemp);
            seperateForm();
        }
    }, [error]);

    useEffect(() => {
        seperateForm();
        window.addEventListener(Constant.event.selectAutoCompleteOption, setMaterials);
        return () => {
            window.removeEventListener(Constant.event.selectAutoCompleteOption, setMaterials);
        };
    }, []);

    // Valeur envoyée au template (vide si erreur)
    const safeMaterial = {
        ...material,
        materiel_categorie: errorInput["materiel-categorie"] ? "" : material.materiel_categorie
    };

    return (
        <div style={{ display: isHidden ? "none" : "block" }}>
            {searchForm.length > 0 && (
                <div className="form-container">
                    <p className="bold form-group">{translate('searchMaterial')}</p>
                    {!isPicked ? (
                        <DynamicFormTemplate
                            key={`characteristic-${material?.materiel_id || "new"}`} 
                            isHidden={false}
                            formData={searchForm}
                            defaultValue={safeMaterial}
                            error={errorInput}
                        />
                    ) : (
                        <div>
                            <DynamicFormTemplate
                                key={`characteristic-${material?.materiel_id || "new"}`} 
                                isHidden={true}
                                formData={searchForm}
                                defaultValue={safeMaterial}
                            />
                            {renderInfo(searchForm)}
                        </div>
                    )}
                    {!isPicked && !isCreateNew ? (
                        <button type="button" className="form-group" onClick={setCreateNewMaterial}>
                            {translate('createNewMaterial')}
                        </button>
                    ) : (
                        <button onClick={unSetCreateNewMaterial} type="button" className="form-group">
                            Changer de matériel
                        </button>
                    )}
                </div>
            )}

            <div className="form-container">
                <h4 className="bold form-group">Matériel : caractéristiques {boutonMateriel}</h4>
                {isCreateNew ? (
                    <DynamicFormTemplate
                        key={`characteristic-${material?.materiel_id || "new"}`} 
                        isHidden={false}
                        formData={characteristicForm}
                        defaultValue={safeMaterial}
                        error={errorInput}
                    />
                ) : (
                    <div>
                        <DynamicFormTemplate
                            key={`characteristic-${material?.materiel_id || "new"}`} 
                            isHidden={true}
                            formData={characteristicForm}
                            defaultValue={safeMaterial}
                        />
                        {renderInfo(characteristicForm)}
                    </div>
                )}

                {!isCreateNew && (
                    <button type="button" className="form-group" onClick={modifiesMaterial}>
                        {translate('editInformation')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default MaterialForm;